import React, { useCallback, useRef, useState } from "react";
import PageHeader from "../components/PageHeader";
import { Button, Field } from "../components/ui/kit";
import { useAuth } from "../context/AuthContext";
import api, { apiError } from "../lib/api";
import { canSee } from "../lib/nav";
import McpConnector from "../components/McpConnector";
import WorkflowUkoly from "../components/WorkflowUkoly";
import FazeNastaveni from "../components/FazeNastaveni";
import TechnologieNastaveni from "../components/TechnologieNastaveni";
import SablonaNabidky from "../components/SablonaNabidky";
import NahratObrazek from "../components/NahratObrazek";
import LogoNahled from "../components/LogoNahled";
import { useUrlStav } from "../lib/navigace";
import Segment from "../components/Segment";
import { useAres, IcoAres, AresProuzek } from "../components/AresPole";

export default function Settings() {
  const { user, company, setCompany, setUser } = useAuth();
  const isOwner = canSee(user, ["OWNER"]);
  const [wfVerze, setWfVerze] = useState(0);     // po uložení fází se šablony úkolů načtou znovu
  // Konektor pro AI: stejné role hlídá backend (MCP_ROLES), subdodavatel ne.
  const canMcp = canSee(user, ["OWNER", "OBCHODNIK", "ASISTENTKA", "TECHNIK"]);
  // Workflow úkolů: mění majitel, obchodník jen vidí (backend CTENI/MAJITEL).
  const canWorkflow = canSee(user, ["OWNER", "OBCHODNIK"]);

  // Záložky jako v Proyekty — každá oblast zvlášť, ne vše na jedné stránce.
  const ZALOZKY = [
    { key: "profil", label: "Můj profil" },
    { key: "firma", label: "Firma" },
    ...(canWorkflow ? [{ key: "technologie", label: "Technologie" }] : []),
    ...(canWorkflow ? [{ key: "workflow", label: "Fáze a úkoly" }] : []),
    ...(canWorkflow ? [{ key: "sablona", label: "Šablona nabídky" }] : []),
    ...(canMcp ? [{ key: "ai", label: "Konektor pro AI" }] : []),
  ];
  const [tabUrl, setTabUrl] = useUrlStav("tab", "profil");
  // Workflow ukládá až tlačítkem — při odchodu z něj s neuloženými změnami se zeptáme.
  const wfNeulozeno = useRef(false);
  const onWfNeulozeno = useCallback((v) => { wfNeulozeno.current = v; }, []);
  const setTab = (t) => {
    if (wfNeulozeno.current && t !== "workflow"
      && !window.confirm("Ve workflow máš neuložené změny. Zahodit je?")) return;
    setTabUrl(t);
  };
  const tab = ZALOZKY.some((z) => z.key === tabUrl) ? tabUrl : "profil";

  const [c, setC] = useState({
    name: company?.name || "",
    ico: company?.ico || "",
    dic: company?.dic || "",
    web: company?.web || "",
    logo_url: company?.logo_url || "",
    logo_velikost: company?.logo_velikost || 100,
    adresa: { ...(company?.adresa || { ulice: "", psc: "", mesto: "" }) },
    zaloha_pct: company?.zaloha_pct ?? 50,
    marze_cil_pct: company?.marze_cil_pct ?? 30,
  });
  const [p, setP] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    pozice: user?.pozice || "",
    osloveni: user?.osloveni || "",
    photo_url: user?.photo_url || "",
  });

  // IČO → ARES doplní název, DIČ a sídlo firmy.
  const ares = useAres();
  const zAres = (a) => {
    ares.prevzit(a, { ...c, ico: a.ico });
    setC((x) => ({ ...x, name: a.nazev, ico: a.ico, dic: a.dic || x.dic, adresa: { ...x.adresa, ...a.adresa } }));
  };
  const sv = ares.firma ? ares.svit : "";
  const [cState, setCState] = useState({ loading: false, saved: false, error: "" });
  const [pState, setPState] = useState({ loading: false, saved: false, error: "" });

  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  // Kdo se přihlašuje jen kódem z e-mailu, heslo nemá (nastaví si ho, když chce).
  const maHeslo = user?.ma_heslo !== false;
  const [pwState, setPwState] = useState({ loading: false, saved: false, error: "" });

  const saveCompany = async (e) => {
    e.preventDefault();
    setCState({ loading: true, saved: false, error: "" });
    try {
      // Prázdné procentní pole se ukládá jako 0 (0 % je platná hodnota).
      const { data } = await api.patch("/api/company", {
        ...c, zaloha_pct: c.zaloha_pct === "" ? 0 : c.zaloha_pct, marze_cil_pct: c.marze_cil_pct === "" ? 0 : c.marze_cil_pct });
      setCompany(data);
      setCState({ loading: false, saved: true, error: "" });
      setTimeout(() => setCState((s) => ({ ...s, saved: false })), 2500);
    } catch (err) {
      setCState({ loading: false, saved: false, error: apiError(err.response?.data?.detail) });
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setPState({ loading: true, saved: false, error: "" });
    try {
      const { data } = await api.patch("/api/users/me", p);
      setUser(data);
      setPState({ loading: false, saved: true, error: "" });
      setTimeout(() => setPState((s) => ({ ...s, saved: false })), 2500);
    } catch (err) {
      setPState({ loading: false, saved: false, error: apiError(err.response?.data?.detail) });
    }
  };

  const [em, setEm] = useState({ new_email: "", password: "" });
  const [emState, setEmState] = useState({ loading: false, sent: false, error: "" });

  const saveEmail = async (e) => {
    e.preventDefault();
    setEmState({ loading: true, sent: false, error: "" });
    try {
      await api.post("/api/auth/change-email", em);
      setEm({ new_email: "", password: "" });
      setEmState({ loading: false, sent: true, error: "" });
    } catch (err) {
      setEmState({ loading: false, sent: false, error: apiError(err.response?.data?.detail) });
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setPwState({ loading: true, saved: false, error: "" });
    try {
      await api.post("/api/auth/change-password", pw);
      setPw({ current_password: "", new_password: "" });
      if (!maHeslo) setUser({ ...user, ma_heslo: true });
      setPwState({ loading: false, saved: true, error: "" });
      setTimeout(() => setPwState((s) => ({ ...s, saved: false })), 2500);
    } catch (err) {
      setPwState({ loading: false, saved: false, error: apiError(err.response?.data?.detail) });
    }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader eyebrow="Nastavení" title="Nastavení" />

      <Segment varianta="podtrzeni" className="mb-5 flex gap-6 border-b border-line overflow-x-auto [scrollbar-width:none]"
        role="tablist" data-testid="settings-tabs">
        {ZALOZKY.map((z) => (
          <button key={z.key} data-on={tab === z.key} onClick={() => setTab(z.key)} role="tab" aria-selected={tab === z.key}
            data-testid={`settings-tab-${z.key}`}
            className={`tap shrink-0 px-0.5 h-11 t-telo font-700 whitespace-nowrap ${
              tab === z.key ? "text-ink" : "text-muted hover:text-ink"}`}>{z.label}</button>
        ))}
      </Segment>

      {tab === "firma" && (
      <div className="max-w-[720px]">
        {/* Company */}
        <form onSubmit={saveCompany} className="card p-5 md:p-6" data-testid="company-form">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-[14.5px] font-700 text-ink">Profil firmy</h2>
          </div>
          <div className="space-y-4">
            <AresProuzek firma={ares.firma} onZrusit={() => setC(ares.zrusit())} />
            <Field label="Název firmy">
              <input className={`field ${sv}`} value={c.name} disabled={!isOwner}
                onChange={(e) => setC({ ...c, name: e.target.value })} data-testid="company-name" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="IČO">
                <IcoAres value={c.ico} disabled={!isOwner} className={sv} testid="company-ico"
                  onChange={(v) => setC((x) => ({ ...x, ico: v }))} onNalezeno={zAres} />
              </Field>
              <Field label="DIČ">
                <input className={`field amount ${sv}`} value={c.dic} disabled={!isOwner}
                  onChange={(e) => setC({ ...c, dic: e.target.value })} data-testid="company-dic" />
              </Field>
            </div>
            <Field label="Ulice a č.p.">
              <input className={`field ${sv}`} value={c.adresa.ulice} disabled={!isOwner}
                onChange={(e) => setC({ ...c, adresa: { ...c.adresa, ulice: e.target.value } })} />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="PSČ">
                <input className={`field amount ${sv}`} value={c.adresa.psc} disabled={!isOwner}
                  onChange={(e) => setC({ ...c, adresa: { ...c.adresa, psc: e.target.value } })} />
              </Field>
              <Field label="Město">
                <input className={`field ${sv}`} value={c.adresa.mesto} disabled={!isOwner}
                  onChange={(e) => setC({ ...c, adresa: { ...c.adresa, mesto: e.target.value } })} />
              </Field>
            </div>
            <Field label="Web">
              <input className="field" value={c.web} disabled={!isOwner}
                onChange={(e) => setC({ ...c, web: e.target.value })} placeholder="https://" />
            </Field>
            <div>
              <span className="label-up text-muted block mb-1.5">Logo</span>
              <NahratObrazek tvar="logo" value={c.logo_url} disabled={!isOwner} testid="company-logo"
                onChange={(v) => setC({ ...c, logo_url: v })} />
              <LogoNahled logo={c.logo_url} nazev={c.name} velikost={c.logo_velikost} disabled={!isOwner}
                onVelikost={(v) => setC({ ...c, logo_velikost: v })} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Výchozí záloha (%)" hint="Použije se při vystavení zálohové faktury.">
                <input type="number" className="field amount" value={c.zaloha_pct} disabled={!isOwner}
                  onChange={(e) => setC({ ...c, zaloha_pct: e.target.value === "" ? "" : Number(e.target.value) })} data-testid="company-zaloha-pct" />
              </Field>
              <Field label="Cíl marže (%)" hint="Cílová marže na dashboardu a v ekonomice.">
                <input type="number" className="field amount" value={c.marze_cil_pct} disabled={!isOwner}
                  onChange={(e) => setC({ ...c, marze_cil_pct: e.target.value === "" ? "" : Number(e.target.value) })} data-testid="company-marze-cil-pct" />
              </Field>
            </div>
          </div>
          {cState.error && <div className="text-[12.5px] text-danger mt-3">{cState.error}</div>}
          {isOwner ? (
            <Button className="mt-5" stav={cState.loading ? "nacita" : cState.saved ? "hotovo" : undefined} disabled={cState.loading} data-testid="company-save">
              Uložit firmu
            </Button>
          ) : (
            <p className="text-[12px] text-muted mt-4">Profil firmy může upravovat jen majitel.</p>
          )}
        </form>
        {isOwner && company?.demo && <ObnovaUkazky />}
      </div>
      )}

      {tab === "profil" && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        {/* Profile */}
        <form onSubmit={saveProfile} className="card p-5 md:p-6 lg:row-span-2" data-testid="profile-form">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-[14.5px] font-700 text-ink">Můj profil</h2>
          </div>
          <div className="space-y-4">
            <Field label="Jméno">
              <input className="field" value={p.name}
                onChange={(e) => setP({ ...p, name: e.target.value })} data-testid="profile-name" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Telefon">
                <input className="field amount" value={p.phone}
                  onChange={(e) => setP({ ...p, phone: e.target.value })} data-testid="profile-phone" />
              </Field>
              <Field label="Oslovení" hint="Např. „Martine“">
                <input className="field" value={p.osloveni}
                  onChange={(e) => setP({ ...p, osloveni: e.target.value })} />
              </Field>
            </div>
            <Field label="Pozice">
              <input className="field" value={p.pozice}
                onChange={(e) => setP({ ...p, pozice: e.target.value })} placeholder="Servisní technik" />
            </Field>
            <div>
              <span className="label-up text-muted block mb-1.5">Fotka</span>
              <NahratObrazek tvar="foto" jmeno={p.name} value={p.photo_url} testid="profile-foto"
                onChange={(v) => setP({ ...p, photo_url: v })} />
            </div>
            <div>
              <span className="label-up text-muted block mb-1.5">E-mail</span>
              <div className="field flex items-center text-muted bg-line-softer cursor-not-allowed">
                {user?.email}
              </div>
            </div>
          </div>
          {pState.error && <div className="text-[12.5px] text-danger mt-3">{pState.error}</div>}
          <Button className="mt-5" stav={pState.loading ? "nacita" : pState.saved ? "hotovo" : undefined} disabled={pState.loading} data-testid="profile-save">
            Uložit profil
          </Button>
        </form>

      {/* Change password */}
      <form onSubmit={savePassword} className="card p-5 md:p-6" data-testid="password-form">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-[14.5px] font-700 text-ink">{maHeslo ? "Změna hesla" : "Nastavit heslo"}</h2>
        </div>
        {!maHeslo && (
          <p className="t-meta text-muted -mt-3 mb-4">Přihlašuješ se kódem z e-mailu. Heslo je jen záloha, nastavit ho nemusíš.</p>
        )}
        <div className="space-y-4">
          {maHeslo && (
            <Field label="Současné heslo">
              <input type="password" className="field" value={pw.current_password}
                onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
                placeholder="••••••••••" data-testid="password-current" required />
            </Field>
          )}
          <Field label="Nové heslo" hint="Alespoň 10 znaků, písmena i číslice.">
            <input type="password" className="field" value={pw.new_password}
              onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
              placeholder="••••••••••" data-testid="password-new" required />
          </Field>
        </div>
        {pwState.error && <div className="text-[12.5px] text-danger mt-3">{pwState.error}</div>}
        <Button className="mt-5" stav={pwState.loading ? "nacita" : pwState.saved ? "hotovo" : undefined} disabled={pwState.loading} data-testid="password-save">
          {maHeslo ? "Změnit heslo" : "Nastavit heslo"}
        </Button>
      </form>

      {/* Change e-mail */}
      <form onSubmit={saveEmail} className="card p-5 md:p-6" data-testid="email-form">
        <h2 className="text-[14.5px] font-700 text-ink mb-5">Změna e-mailu</h2>
        <div className="space-y-4">
          <Field label="Nový e-mail" hint="Pošleme na něj potvrzovací odkaz.">
            <input type="email" className="field" value={em.new_email}
              onChange={(e) => setEm({ ...em, new_email: e.target.value })}
              data-testid="email-new" required />
          </Field>
          {maHeslo && (
            <Field label="Heslo">
              <input type="password" className="field" value={em.password}
                onChange={(e) => setEm({ ...em, password: e.target.value })}
                placeholder="••••••••••" data-testid="email-password" required />
            </Field>
          )}
        </div>
        {emState.error && <div className="text-[12.5px] text-danger mt-3">{emState.error}</div>}
        {emState.sent && (
          <div className="text-[12.5px] text-success mt-3" data-testid="email-sent">
            Odkaz je na cestě. E-mail se změní, až ho potvrdíš.
          </div>
        )}
        <Button className="mt-5 tap" stav={emState.loading ? "nacita" : undefined} disabled={emState.loading} data-testid="email-save">
          Změnit e-mail
        </Button>
      </form>
      </div>
      )}

      {tab === "technologie" && <TechnologieNastaveni muzeUpravit={isOwner} />}
      {tab === "sablona" && <SablonaNabidky muzeUpravit={isOwner} />}
      {tab === "workflow" && (
        <>
          <FazeNastaveni muzeUpravit={isOwner} onUlozeno={() => setWfVerze((v) => v + 1)} />
          <WorkflowUkoly key={wfVerze} muzeUpravit={isOwner} onNeulozeno={onWfNeulozeno} />
        </>
      )}
      {tab === "ai" && <McpConnector />}
    </div>
  );
}


// Jen ukázková firma: vrátí data do výchozího stavu, ať jde testovat cestu
// zakázky znovu od začátku (server skutečnou firmu obnovit nedovolí).
function ObnovaUkazky() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const obnovit = async () => {
    if (!window.confirm("Obnovit ukázková data?\n\nSmažou se všechna data ukázkové firmy (klienti, případy, nabídky, "
      + "doklady, servis, sklad) i pozvaní uživatelé a nahrají se znovu ukázková. Vrátit to nejde.")) return;
    setBusy(true); setErr("");
    try { await api.post("/api/company/obnovit-ukazku"); window.location.assign("/"); }
    catch (e) { setErr(apiError(e.response?.data?.detail) || e.message); setBusy(false); }
  };
  return (
    <div className="card p-5 md:p-6 mt-4" data-testid="obnova-ukazky">
      <h2 className="t-sekce text-ink font-700">Ukázková data</h2>
      <p className="t-meta text-muted mt-1">Tahle firma je ukázková. Obnovou se data vrátí do výchozího stavu — případy
        v každé fázi i s nabídkami — a cestu zakázky jde testovat znovu od začátku.</p>
      {err && <p className="t-meta text-danger mt-2">{err}</p>}
      <Button className="mt-4 w-full md:w-auto" variant="ghost" onClick={obnovit} disabled={busy} data-testid="obnovit-ukazku">
        {busy ? "Obnovuji…" : "Obnovit ukázková data"}
      </Button>
    </div>
  );
}
